/**
 * Project Material Sheet (PMS) — Phase 1-5
 * Pre-generation wizard, full editability, cap overrides, alt-groups, client-supplied,
 * PMS reconciliation tab, gaps forecaster, backfill wizard.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Sparkles, Loader2, CheckCircle2, AlertTriangle, X,
  Download, Edit3, ChevronDown, ChevronRight, RefreshCw,
  PackageCheck, FlaskConical, Lightbulb, Repeat2,
  Search, Link, SkipForward, PlusCircle, Zap, CheckCheck,
  History, Trash2, Plus, Tag, Shield, ArrowUpRight, Settings2,
  BarChart2, TrendingUp, Upload, FileSpreadsheet
} from 'lucide-react';
import { apiGet, apiPost, apiPut, apiDel, apiUpload } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { toast } from 'sonner';
import AIJobProgress from '../components/AIJobProgress';
import { useUrlTab } from '../lib/hooks/useUrlState';

/* ── constants ────────────────────────────────────────────────────── */
const STATUS_LABEL = {
  pending: 'Pending', generated: 'Awaiting Planner Approval',
  planner_approved: 'Awaiting PM Approval', pm_approved: 'Awaiting MD/CEO Approval',
  md_approved: 'Fully Approved', rejected: 'Rejected',
};
const STATUS_COLOR = {
  pending: 'bg-slate-100 text-slate-600',
  generated: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  planner_approved: 'bg-sky-50 text-sky-700 border-sky-200',
  pm_approved: 'bg-amber-50 text-amber-700 border-amber-200',
  md_approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
};
const PASS_LABEL = { 1: 'Purchasable', 2: 'Derived', 3: 'Consumable', 4: 'Alternative' };
const PASS_COLOR = {
  1: 'bg-blue-50 text-blue-700 border-blue-200',
  2: 'bg-amber-50 text-amber-700 border-amber-200',
  3: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  4: 'bg-violet-50 text-violet-700 border-violet-200',
};
const PASS_ICON = {
  1: <PackageCheck size={10} />,
  2: <FlaskConical size={10} />,
  3: <Lightbulb size={10} />,
  4: <Repeat2 size={10} />,
};

const MATCH_STATUS_COLOR = {
  pending:   'bg-slate-100 text-slate-500 border-slate-200',
  matched:   'bg-sky-50 text-sky-700 border-sky-200',
  review:    'bg-amber-50 text-amber-700 border-amber-200',
  unmatched: 'bg-rose-50 text-rose-700 border-rose-200',
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  overridden:'bg-teal-50 text-teal-700 border-teal-200',
  new_master:'bg-violet-50 text-violet-700 border-violet-200',
  deferred:  'bg-slate-50 text-slate-500 border-slate-200',
};
const MATCH_STATUS_LABEL = {
  pending: 'Not matched', matched: 'Auto-matched', review: 'Needs review',
  unmatched: 'No match', confirmed: 'Confirmed', overridden: 'Overridden',
  new_master: 'New master', deferred: 'Deferred / N/A',
};

const ElloraAI = () => (
  <span className="inline-flex items-center gap-1 text-[10px] font-display font-semibold text-[#3b4872] bg-indigo-50 border border-indigo-100 rounded-sm px-1.5 py-0.5" data-testid="ellora-ai-badge">
    <Sparkles size={10} /> Powered by Ellora AI
  </span>
);

const fmt = (n) => {
  if (n == null || n === '') return '—';
  const num = Number(n);
  if (isNaN(num)) return '—';
  return num.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const fmtCr = (n) => {
  const num = Number(n) || 0;
  if (num >= 1e7) return `₹${(num / 1e7).toFixed(2)} Cr`;
  if (num >= 1e5) return `₹${(num / 1e5).toFixed(1)} L`;
  return `₹${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};


/* ── main component ────────────────────────────────────────────────── */
export default function StepMaterialSheet({ plan, reload, setWorking: _setWorking }) {
  const unlocked = plan.micro_status === 'md_approved' &&
    ['material_sheet', 'completed'].includes(plan.phase);

  const [sheet, setSheet] = useState(null);
  const [sections, setSections] = useState([]);
  const [loadingSheet, setLoadingSheet] = useState(true);
  const [jobId, setJobId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approving, setApproving] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [matchSummary, setMatchSummary] = useState(null);
  const [runningMatch, setRunningMatch] = useState(false);
  const [showMatchPanel, setShowMatchPanel] = useState(false);
  const [boqGaps, setBoqGaps] = useState(null);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  // Phase 4 new state
  const [showGenWizard, setShowGenWizard] = useState(false);
  const [genInputs, setGenInputs] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [addItemSection, setAddItemSection] = useState('');
  const [capOverrides, setCapOverrides] = useState([]);
  const [showCapOverrides, setShowCapOverrides] = useState(false);
  // Phase 5 new state
  const [activeTab, setActiveTab] = useUrlTab('tab', 'sheet');
  const [reconciliation, setReconciliation] = useState(null);
  const [gapsForecast, setGapsForecast] = useState(null);
  const [loadingRecon, setLoadingRecon] = useState(false);
  const [showBackfillWizard, setShowBackfillWizard] = useState(false);

  const loadSheet = useCallback(async () => {
    setLoadingSheet(true);
    try {
      const data = await apiGet(`/project-planning/plans/${plan.id}/material-sheet`);
      setSheet(data.sheet || null);
      setSections(data.sections || []);
    } catch {
      setSheet(null); setSections([]);
    } finally { setLoadingSheet(false); }
  }, [plan.id]);

  const loadMatchSummary = useCallback(async () => {
    try {
      const data = await apiGet(`/project-planning/plans/${plan.id}/material-sheet/match-summary`);
      setMatchSummary(data);
    } catch { setMatchSummary(null); }
  }, [plan.id]);

  useEffect(() => { loadSheet(); }, [loadSheet]);
  useEffect(() => {
    if (sheet) loadMatchSummary();
  }, [sheet, loadMatchSummary]);

  const loadBoqGaps = useCallback(async () => {
    try {
      const data = await apiGet(`/project-planning/plans/${plan.id}/material-sheet/boq-gaps`);
      setBoqGaps(data);
    } catch { setBoqGaps(null); }
  }, [plan.id]);

  useEffect(() => {
    if (sheet && sheet.pms_status !== 'md_approved') loadBoqGaps();
  }, [sheet, loadBoqGaps]);

  const generate = async (inputs) => {
    setShowGenWizard(false);
    setGenerating(true);
    try {
      const body = inputs ? { pms_generation_inputs: inputs } : {};
      const r = await apiPost(`/project-planning/plans/${plan.id}/generate-material-sheet`, body);
      setJobId(r.job_id);
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Generation failed');
      setGenerating(false);
    }
  };

  const openGenerateWizard = () => setShowGenWizard(true);

  const onJobDone = () => {
    setJobId(null); setGenerating(false);
    loadSheet(); reload();
    toast.success('Material Sheet generated — run matching and review before approving');
  };

  const onJobFail = (err) => {
    setJobId(null); setGenerating(false);
    toast.error(err || 'Material Sheet generation failed');
  };

  const approve = async () => {
    setApproving(true);
    try {
      const r = await apiPost(`/project-planning/plans/${plan.id}/material-sheet/approve`, {});
      toast.success(`Approved → ${STATUS_LABEL[r.pms_status] || r.pms_status}`);
      loadSheet(); loadMatchSummary(); reload();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Approval failed');
    } finally { setApproving(false); }
  };

  const runMatching = async () => {
    setRunningMatch(true);
    try {
      const r = await apiPost(`/project-planning/plans/${plan.id}/material-sheet/run-matching`, {});
      toast.success(`Matching complete: ${r.matched} auto-matched, ${r.review} need review, ${r.unmatched} unmatched (${r.master_count} masters)`);
      loadMatchSummary();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Matching failed');
    } finally { setRunningMatch(false); }
  };

  const confirmAllAuto = async () => {
    try {
      const r = await apiPost(`/project-planning/plans/${plan.id}/material-sheet/confirm-all-auto`, {});
      toast.success(`${r.confirmed_count} auto-matches confirmed`);
      loadMatchSummary();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Failed');
    }
  };

  const reject = async () => {
    if (!rejectReason.trim()) { toast.error('Rejection reason is required'); return; }
    try {
      await apiPost(`/project-planning/plans/${plan.id}/material-sheet/reject`, { reason: rejectReason });
      toast.success('Material Sheet rejected — sent back for revision');
      setShowReject(false); setRejectReason('');
      loadSheet(); reload();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Rejection failed');
    }
  };

  const saveEdit = async () => {
    if (!editItem) return;
    try {
      await apiPut(`/project-planning/plans/${plan.id}/material-sheet/items`, {
        items: [editItem],
      });
      toast.success('Item updated');
      setEditItem(null); loadSheet();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Save failed');
    }
  };

  const deleteItem = async (itemId, itemName) => {
    if (!window.confirm(`Delete "${itemName}"? This cannot be undone.`)) return;
    try {
      await apiDel(`/project-planning/plans/${plan.id}/material-sheet/items/${itemId}`);
      toast.success(`"${itemName}" deleted`);
      loadSheet();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Delete failed');
    }
  };

  const addItem = async (newItem) => {
    try {
      await apiPost(`/project-planning/plans/${plan.id}/material-sheet/items/add`, newItem);
      toast.success('Item added');
      setShowAddItem(false);
      loadSheet();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Add failed');
    }
  };

  const loadCapOverrides = useCallback(async () => {
    try {
      const data = await apiGet(`/project-planning/plans/${plan.id}/material-sheet/cap-overrides`);
      setCapOverrides(data.overrides || []);
    } catch { setCapOverrides([]); }
  }, [plan.id]);

  const approveCapOverride = async (overrideId) => {
    try {
      await apiPost(`/project-planning/plans/${plan.id}/material-sheet/cap-overrides/${overrideId}/approve`, {});
      toast.success('Cap override approved — PMS item updated');
      loadCapOverrides(); loadSheet();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Approve failed');
    }
  };

  const rejectCapOverride = async (overrideId, reason) => {
    try {
      await apiPost(`/project-planning/plans/${plan.id}/material-sheet/cap-overrides/${overrideId}/reject`, { reason });
      toast.success('Cap override rejected');
      loadCapOverrides();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Reject failed');
    }
  };

  useEffect(() => {
    if (sheet?.pms_status === 'md_approved') loadCapOverrides();
  }, [sheet, loadCapOverrides]);

  const loadReconciliation = useCallback(async () => {
    setLoadingRecon(true);
    try {
      const [recon, gaps] = await Promise.all([
        apiGet(`/project-planning/plans/${plan.id}/material-sheet/reconciliation`),
        apiGet(`/project-planning/plans/${plan.id}/material-sheet/gaps-forecast`),
      ]);
      setReconciliation(recon);
      setGapsForecast(gaps);
    } catch { toast.error('Could not load reconciliation data'); }
    finally { setLoadingRecon(false); }
  }, [plan.id]);

  useEffect(() => {
    if ((activeTab === 'reconciliation' || activeTab === 'forecast') && sheet && !reconciliation) {
      loadReconciliation();
    }
  }, [activeTab, sheet, reconciliation, loadReconciliation]);

  const toggleSection = (sec) =>
    setCollapsedSections(p => ({ ...p, [sec]: !p[sec] }));

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const xlsxUrl = `${backendUrl}/api/project-planning/plans/${plan.id}/material-sheet.xlsx`;

  /* ── locked state ─────────────────────────────────────────────── */
  if (!unlocked) {
    return (
      <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-sm"
        data-testid="pp-pms-locked">
        <div className="text-xs uppercase tracking-widest font-display text-slate-400 mb-2">
          Material Sheet unlocks after schedules are approved &amp; finalised
        </div>
        <p className="text-sm text-slate-500 max-w-lg mx-auto">
          Complete the Micro-schedule 3-stage approval and finalise the WBS. The Project Material Sheet
          is generated last because its rows cross-reference the BOQ, WBS, and schedules.
        </p>
      </div>
    );
  }

  /* ── loading ──────────────────────────────────────────────────── */
  if (loadingSheet) {
    return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  }

  /* ── no sheet yet ─────────────────────────────────────────────── */
  if (!sheet && !generating && !jobId) {
    return (
      <>
        <div className="text-center py-16 bg-white border border-dashed border-indigo-200 rounded-sm"
          data-testid="pp-pms-stub">
          <div className="flex justify-center mb-4"><ElloraAI /></div>
          <div className="text-sm font-display font-semibold text-[#3b4872] mb-2">
            Project Material Sheet — AI Generation Ready
          </div>
          <p className="text-xs text-slate-500 max-w-sm mx-auto mb-6">
            Ellora AI will generate the single source of truth for ALL project materials:
            BOQ purchasables, derived cement/sand/aggregate rows with norms, enabling consumables,
            and procurement cap (MAX order quantity) for each item.
          </p>
          <div className="flex items-center gap-3 justify-center flex-wrap mb-4">
            {[
              { icon: <PackageCheck size={13} className="text-blue-600" />, label: 'BOQ Purchasables', color: 'bg-blue-50 text-blue-700' },
              { icon: <FlaskConical size={13} className="text-amber-600" />, label: 'Derived (Norms)', color: 'bg-amber-50 text-amber-700' },
              { icon: <Lightbulb size={13} className="text-emerald-600" />, label: 'Consumables', color: 'bg-emerald-50 text-emerald-700' },
              { icon: <Repeat2 size={13} className="text-violet-600" />, label: 'Alternatives', color: 'bg-violet-50 text-violet-700' },
            ].map(t => (
              <span key={t.label} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-semibold border ${t.color}`}>
                {t.icon}{t.label}
              </span>
            ))}
          </div>
          <button onClick={openGenerateWizard}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#3b4872] hover:bg-[#2d3856] text-white text-sm font-display font-bold rounded-sm"
            data-testid="pp-pms-generate-btn">
            <Settings2 size={14} /> Set Up &amp; Generate Material Sheet
          </button>
        </div>
        {/* Bug fix: this early return previously exited before the wizard modal render
            further down the tree, so clicking the button above set showGenWizard=true
            but the modal never mounted — the very first generation looked unresponsive. */}
        {showGenWizard && (
          <GenerationInputWizard
            existingInputs={genInputs}
            onGenerate={(inputs) => { setGenInputs(inputs); generate(inputs); }}
            onClose={() => setShowGenWizard(false)}
            hasExistingSheet={!!sheet}
          />
        )}
      </>
    );
  }

  /* ── generating (job in progress) ────────────────────────────── */
  if (generating || jobId) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="pp-pms-generating">
        <div className="flex items-center gap-2 mb-4">
          <ElloraAI />
          <span className="text-sm font-display font-semibold text-[#3b4872]">
            Generating Project Material Sheet…
          </span>
        </div>
        {jobId && (
          <AIJobProgress jobId={jobId} onComplete={onJobDone} onFail={onJobFail}
            label="Ellora AI is analysing BOQ items and deriving material norms" />
        )}
      </div>
    );
  }

  /* ── sheet exists ─────────────────────────────────────────────── */
  const pmsStatus = sheet?.pms_status || 'pending';
  const isApproved = pmsStatus === 'md_approved';
  const canApprove = ['generated', 'planner_approved', 'pm_approved'].includes(pmsStatus);

  const totalItems = sections.reduce((s, sec) => s + (sec.items?.length || 0), 0);
  const passCount = { 1: 0, 2: 0, 3: 0, 4: 0 };
  sections.forEach(sec => (sec.items || []).forEach(it => {
    const p = it.pass_source || 1;
    passCount[p] = (passCount[p] || 0) + 1;
  }));

  return (
    <div className="space-y-5" data-testid="pp-pms-sheet">

      {/* ── Status bar ─────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <ElloraAI />
            <span className={`px-2.5 py-0.5 rounded-sm text-[10px] font-display font-bold uppercase tracking-widest border ${STATUS_COLOR[pmsStatus] || 'bg-slate-100'}`}
              data-testid="pp-pms-status-badge">
              {STATUS_LABEL[pmsStatus] || pmsStatus}
            </span>
            {pmsStatus === 'rejected' && sheet?.rejection_reason && (
              <span className="text-xs text-rose-600 flex items-center gap-1">
                <AlertTriangle size={11} /> {sheet.rejection_reason}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!isApproved && (
              <button onClick={openGenerateWizard}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-semibold text-slate-600 bg-white border border-slate-300 rounded-sm hover:border-[#3b4872] hover:text-[#3b4872]"
                data-testid="pp-pms-regen-btn">
                <RefreshCw size={12} /> Regenerate
              </button>
            )}
            <a href={xlsxUrl} target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-semibold text-slate-600 bg-white border border-slate-300 rounded-sm hover:border-[#3b4872] hover:text-[#3b4872]"
              data-testid="pp-pms-download-btn">
              <Download size={12} /> Export .xlsx
            </a>
            {canApprove && (
              <>
                <button onClick={approve} disabled={approving}
                  className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-display font-bold bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm disabled:opacity-50"
                  data-testid="pp-pms-approve-btn">
                  {approving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                  {pmsStatus === 'generated' ? 'Planner Approve' : pmsStatus === 'planner_approved' ? 'PM Approve' : 'MD Approve'}
                </button>
                <button onClick={() => setShowReject(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-semibold text-rose-600 bg-rose-50 border border-rose-200 rounded-sm hover:bg-rose-100"
                  data-testid="pp-pms-reject-btn">
                  <X size={12} /> Reject
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── Approval stepper ─────────────────────────────────── */}
        <div className="flex items-center gap-0 mt-3 text-[10px] font-display">
          {[
            { v: 'generated', label: 'Generated' },
            { v: 'planner_approved', label: 'Planner ✓' },
            { v: 'pm_approved', label: 'PM ✓' },
            { v: 'md_approved', label: 'MD ✓' },
          ].map((step, i) => {
            const flow = ['pending','generated','planner_approved','pm_approved','md_approved'];
            const curIdx = flow.indexOf(pmsStatus);
            const stepIdx = flow.indexOf(step.v);
            const done = curIdx > stepIdx;
            const active = curIdx === stepIdx;
            return (
              <React.Fragment key={step.v}>
                {i > 0 && <div className={`h-px w-8 ${done ? 'bg-emerald-400' : 'bg-slate-200'}`} />}
                <div className={`px-2 py-0.5 rounded-sm font-bold uppercase tracking-wider ${
                  done ? 'bg-emerald-50 text-emerald-700' :
                  active ? 'bg-[#3b4872] text-white' : 'bg-slate-50 text-slate-400'}`}>
                  {step.label}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ── KPI strip ─────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="pp-pms-kpis">
        {[
          { label: 'Total Items', value: totalItems, testId: 'pp-pms-kpi-items' },
          { label: 'Total Cap Value', value: fmtCr(sheet?.total_cap_value), testId: 'pp-pms-kpi-value' },
          { label: 'BOQ Purchasables', value: passCount[1], badge: 'bg-blue-50 text-blue-700', testId: 'pp-pms-kpi-p1' },
          { label: 'Derived (Norms)', value: passCount[2], badge: 'bg-amber-50 text-amber-700', testId: 'pp-pms-kpi-p2' },
          { label: 'Consumables', value: passCount[3] + passCount[4], badge: 'bg-emerald-50 text-emerald-700', testId: 'pp-pms-kpi-p3' },
        ].map(k => (
          <div key={k.label} className={`bg-white border border-slate-200 rounded-sm p-3 ${k.badge || ''}`}
            data-testid={k.testId}>
            <div className="text-[10px] uppercase tracking-widest font-display text-slate-500 mb-0.5">{k.label}</div>
            <div className="text-xl font-display font-bold text-[#3b4872]">{k.value}</div>
          </div>
        ))}
      </div>

      {/* ── Tab navigation (Phase 5) ─────────────────────────── */}
      {sheet && (
        <div className="flex border-b border-slate-200 bg-white rounded-t-sm overflow-hidden">
          {[
            { key: 'sheet', label: 'Material Sheet', icon: <PackageCheck size={13} /> },
            { key: 'reconciliation', label: 'Reconciliation', icon: <BarChart2 size={13} /> },
            { key: 'forecast', label: 'Gaps Forecast', icon: <TrendingUp size={13} /> },
          ].map(tab => (
            <button key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`pp-pms-tab-${tab.key}`}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-display font-semibold border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-[#3b4872] text-[#3b4872] bg-indigo-50/40'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Reconciliation Tab ─────────────────────────────── */}
      {activeTab === 'reconciliation' && (
        <ReconciliationTab
          reconciliation={reconciliation}
          loading={loadingRecon}
          onRefresh={loadReconciliation}
          planId={plan.id}
          onBackfill={() => setShowBackfillWizard(true)}
          token={null}
        />
      )}

      {/* ── Gaps Forecast Tab ─────────────────────────────── */}
      {activeTab === 'forecast' && (
        <GapsForecastTab
          forecast={gapsForecast}
          loading={loadingRecon}
          onRefresh={loadReconciliation}
        />
      )}

      {/* ── Main Sheet Tab ─────────────────────────────────── */}
      {activeTab === 'sheet' && (<>

      {/* ── Match Review Panel ─────────────────────────────── */}
      {!isApproved && (
        <MatchReviewPanel
          planId={plan.id}
          matchSummary={matchSummary}
          runningMatch={runningMatch}
          showMatchPanel={showMatchPanel}
          setShowMatchPanel={setShowMatchPanel}
          onRunMatching={runMatching}
          onConfirmAllAuto={confirmAllAuto}
          onActionDone={loadMatchSummary}
        />
      )}

      {/* ── BOQ Gap Warning ──────────────────────────────── */}
      {!isApproved && boqGaps?.has_gaps && (
        <BoqGapPanel gaps={boqGaps.gaps} />
      )}

      {/* ── Norms/Basis note ─────────────────────────────────── */}
      {sheet?.order_qty_basis && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800 leading-relaxed"
          data-testid="pp-pms-basis-note">
          <strong className="font-display">Order Quantity Basis: </strong>{sheet.order_qty_basis}
        </div>
      )}

      {/* ── Material table (sections) ─────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="pp-pms-table">
        {/* Column headers */}
        <div className="grid text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200"
          style={{ gridTemplateColumns: '3rem 1fr 7rem 7rem 6rem 8rem 7rem 7rem 2.5rem' }}>
          <div className="px-2 py-2 border-r border-slate-200 text-center">Sr.</div>
          <div className="px-3 py-2 border-r border-slate-200">Material / Specification</div>
          <div className="px-2 py-2 border-r border-slate-200 text-center">BOQ Qty</div>
          <div className="px-2 py-2 border-r border-slate-200 text-center">Order Cap (MAX)</div>
          <div className="px-2 py-2 border-r border-slate-200 text-center">UOM</div>
          <div className="px-2 py-2 border-r border-slate-200 text-center">Rate (₹)</div>
          <div className="px-2 py-2 border-r border-slate-200 text-center">Amount (₹)</div>
          <div className="px-2 py-2 border-r border-slate-200 text-center">Total+GST (₹)</div>
          <div className="px-2 py-2 text-center">Type</div>
        </div>

        {sections.map((sec) => (
          <div key={sec.section} data-testid={`pp-pms-section-${sec.section_order}`}>
            {/* Section header */}
            <div className="w-full flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-t border-slate-200 text-xs font-display font-bold text-[#3b4872]">
              <button
                className="flex items-center gap-1.5 flex-1 text-left hover:text-[#2d3856]"
                onClick={() => toggleSection(sec.section)}
                data-testid={`pp-pms-sec-toggle-${sec.section_order}`}>
                {collapsedSections[sec.section]
                  ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                {sec.section}
                <span className="ml-2 text-slate-400 font-normal text-[10px]">
                  {sec.items?.length || 0} items ·{' '}
                  {fmtCr(sec.items?.reduce((s, it) => s + (it.total || 0), 0))}
                </span>
              </button>
              {!isApproved && (
                <button
                  onClick={() => { setAddItemSection(sec.section); setShowAddItem(true); }}
                  className="flex items-center gap-1 px-2 py-0.5 text-[10px] text-indigo-600 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100 font-semibold ml-auto"
                  data-testid={`pp-pms-add-item-${sec.section_order}`}>
                  <Plus size={10} /> Add Row
                </button>
              )}
            </div>

            {!collapsedSections[sec.section] && (sec.items || []).map((it) => (
              <PMSItemRow key={it.id} item={it} isApproved={isApproved}
                onEdit={() => setEditItem({ ...it })}
                onDelete={() => deleteItem(it.id, it.name)}
                planId={plan.id}
                onCapOverrideSubmit={loadCapOverrides} />
            ))}
          </div>
        ))}

        {/* Total row */}
        <div className="flex items-center justify-between px-3 py-2.5 bg-[#3b4872] text-white"
          data-testid="pp-pms-total-row">
          <span className="text-xs font-display font-bold uppercase tracking-widest">TOTAL PROJECT MATERIAL CAP</span>
          <span className="text-base font-display font-bold">{fmtCr(sheet?.total_cap_value)}</span>
        </div>
      </div>

      {/* ── Assumptions ─────────────────────────────────────── */}
      {sheet?.assumptions?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="pp-pms-assumptions">
          <div className="text-xs font-display font-bold uppercase tracking-widest text-slate-500 mb-2">
            Norms &amp; Assumptions
          </div>
          <ul className="text-xs text-slate-600 space-y-1">
            {sheet.assumptions.map((a, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-[#3b4872] font-bold mt-0.5">{i + 1}.</span>{a}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Cap Override Panel (md_approved only) ─────────────── */}
      {isApproved && (
        <CapOverridePanel
          overrides={capOverrides}
          onApprove={approveCapOverride}
          onReject={rejectCapOverride}
          show={showCapOverrides}
          onToggle={() => setShowCapOverrides(p => !p)}
        />
      )}

      {/* ── Audit Trail ─────────────────────────────────────── */}
      <AuditTrailSection
        sheet={sheet}
        items={sections.flatMap(s => s.items || [])}
        show={showAuditTrail}
        onToggle={() => setShowAuditTrail(p => !p)}
      />

      </>)} {/* end Sheet tab */}

      {/* ── Edit modal ─────────────────────────────────────── */}
      {editItem && (
        <EditModal item={editItem} setItem={setEditItem} onSave={saveEdit} onClose={() => setEditItem(null)} />
      )}

      {/* ── Reject modal ─────────────────────────────────────── */}
      {showReject && (
        <RejectModal reason={rejectReason} setReason={setRejectReason}
          onCancel={() => { setShowReject(false); setRejectReason(''); }}
          onSubmit={reject} />
      )}

      {/* ── Generation wizard ────────────────────────────────── */}
      {showGenWizard && (
        <GenerationInputWizard
          existingInputs={genInputs}
          onGenerate={(inputs) => { setGenInputs(inputs); generate(inputs); }}
          onClose={() => setShowGenWizard(false)}
          hasExistingSheet={!!sheet}
        />
      )}

      {/* ── Add item modal ────────────────────────────────────── */}
      {showAddItem && (
        <AddItemModal
          section={addItemSection}
          onSave={addItem}
          onClose={() => setShowAddItem(false)}
        />
      )}

      {/* ── Backfill Wizard modal ─────────────────────────────── */}
      {showBackfillWizard && (
        <BackfillWizard
          planId={plan.id}
          onClose={() => setShowBackfillWizard(false)}
          onDone={() => { setShowBackfillWizard(false); loadSheet(); loadReconciliation(); }}
        />
      )}
    </div>
  );
}


/* ── PMS Item row ─────────────────────────────────────────────────── */
function PMSItemRow({ item, isApproved, onEdit, onDelete, planId, onCapOverrideSubmit }) {
  const ps = item.pass_source || 1;
  const rowBg = ps === 2 ? 'bg-amber-50/40' : ps === 3 ? 'bg-emerald-50/40' : ps === 4 ? 'bg-violet-50/40' : '';
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [overrideQty, setOverrideQty] = useState('');
  const [overrideReason, setOverrideReason] = useState('');

  const submitOverride = async () => {
    if (!overrideQty || !overrideReason.trim()) { toast.error('Both new qty and reason are required'); return; }
    try {
      await apiPost(`/project-planning/plans/${planId}/material-sheet/items/${item.id}/request-cap-override`, {
        new_order_qty: parseFloat(overrideQty),
        reason: overrideReason,
      });
      toast.success('Cap override request submitted — awaiting PM/MD approval');
      setShowOverrideForm(false); setOverrideQty(''); setOverrideReason('');
      if (onCapOverrideSubmit) onCapOverrideSubmit();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Failed to submit override request');
    }
  };

  return (
    <div className={`border-b border-slate-100 group ${rowBg}`}
      data-testid={`pp-pms-item-${item.sr_no}`}>
      <div className="grid hover:bg-slate-50/60"
        style={{ gridTemplateColumns: '3rem 1fr 7rem 7rem 6rem 8rem 7rem 7rem 2.5rem' }}>
        <div className="px-2 py-2 text-[10px] text-slate-400 font-display text-center border-r border-slate-100">
          {item.sr_no}
        </div>
        <div className="px-3 py-2 border-r border-slate-100">
          <div className="flex items-start gap-1.5">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-display font-semibold text-slate-800 leading-snug">{item.name}</div>
              {item.spec && <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{item.spec}</div>}
              {item.basis && (
                <div className="text-[10px] text-amber-700 mt-1 leading-tight bg-amber-50/60 rounded px-1 py-0.5 border border-amber-100">
                  <span className="font-semibold">Basis:</span> {item.basis}
                </div>
              )}
              {item.boq_ref && (
                <div className="text-[10px] text-slate-400 mt-0.5">
                  BOQ: {item.boq_ref}{item.wbs_ref ? ` · WBS: ${item.wbs_ref}` : ''}
                </div>
              )}
              {/* Badges row */}
              <div className="flex flex-wrap gap-1 mt-1">
                {item.matched_master_code && (
                  <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-display font-bold border ${MATCH_STATUS_COLOR[item.match_status] || 'bg-slate-50 text-slate-500 border-slate-200'}`}
                    data-testid={`pp-pms-master-badge-${item.sr_no}`}
                    title={`Matched to: ${item.matched_master_name}`}>
                    <Link size={8} /> {item.matched_master_code}
                  </div>
                )}
                {item.match_status === 'deferred' && (
                  <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-display font-bold border bg-slate-50 text-slate-400 border-slate-200">
                    <SkipForward size={8} /> Deferred / N/A
                  </div>
                )}
                {(!item.match_status || item.match_status === 'pending') && (
                  <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] border bg-slate-50 text-slate-400 border-slate-200">
                    Not matched
                  </div>
                )}
                {item.client_supplied && (
                  <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-display font-bold border bg-sky-50 text-sky-700 border-sky-200"
                    data-testid={`pp-pms-client-supplied-${item.sr_no}`} title="Client-supplied — exempt from procurement caps">
                    <Shield size={8} /> Client-supplied
                  </div>
                )}
                {item.alternative_group_id && (
                  <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-display font-bold border bg-violet-50 text-violet-700 border-violet-200"
                    data-testid={`pp-pms-alt-group-${item.sr_no}`} title={`Alternative group: ${item.alternative_group_id}`}>
                    <Tag size={8} /> Alt {item.alternative_group_id}
                  </div>
                )}
              </div>
              {(item.brand_options || []).length > 0 && (
                <div className="text-[10px] text-slate-400 mt-0.5">
                  Brands: {item.brand_options.join(' / ')}
                </div>
              )}
            </div>
          </div>

          {/* Cap override request form (md_approved only) */}
          {isApproved && item.matched_master_id && (
            <div className="mt-1">
              {showOverrideForm ? (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 mt-1 space-y-1.5" data-testid={`pp-pms-override-form-${item.sr_no}`}>
                  <div className="text-[10px] font-display font-bold text-amber-800">Request Cap Override (Re-measurement)</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <input type="number" value={overrideQty} onChange={e => setOverrideQty(e.target.value)}
                        placeholder={`New qty (current: ${item.order_qty})`}
                        className="w-full border border-amber-300 rounded px-2 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-amber-400"
                        data-testid={`pp-pms-override-qty-${item.sr_no}`} />
                    </div>
                    <span className="text-[10px] text-amber-700">{item.uom_order}</span>
                  </div>
                  <input value={overrideReason} onChange={e => setOverrideReason(e.target.value)}
                    placeholder="Reason: re-measurement, scope change, etc."
                    className="w-full border border-amber-300 rounded px-2 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-amber-400"
                    data-testid={`pp-pms-override-reason-${item.sr_no}`} />
                  <div className="flex gap-2">
                    <button onClick={submitOverride} className="px-3 py-1 text-[10px] font-display font-bold bg-amber-600 hover:bg-amber-700 text-white rounded"
                      data-testid={`pp-pms-override-submit-${item.sr_no}`}>Submit Request</button>
                    <button onClick={() => setShowOverrideForm(false)} className="px-3 py-1 text-[10px] text-slate-600 bg-white border border-slate-300 rounded">Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowOverrideForm(true)}
                  className="opacity-0 group-hover:opacity-100 flex items-center gap-1 text-[9px] text-amber-600 hover:text-amber-800 mt-0.5"
                  data-testid={`pp-pms-override-btn-${item.sr_no}`}>
                  <ArrowUpRight size={9} /> Request Cap Override
                </button>
              )}
            </div>
          )}
        </div>
        <div className="px-2 py-2 text-[10px] text-slate-600 text-right border-r border-slate-100">
          {fmt(item.boq_qty)}
          <div className="text-[9px] text-slate-400">{item.uom_boq}</div>
        </div>
        <div className="px-2 py-2 border-r border-slate-100">
          <div className="text-xs font-display font-bold text-[#3b4872] text-right">{fmt(item.order_qty)}</div>
          <div className="text-[9px] text-slate-400 text-right">{item.uom_order} (MAX cap)</div>
          {item.ordered_qty > 0 && (
            <>
              <div className="text-[9px] text-emerald-600 text-right font-semibold">
                Ordered: {fmt(item.ordered_qty)}
              </div>
              {item.order_qty > 0 && (
                <div className="mt-0.5 h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${(item.ordered_qty / item.order_qty) > 0.85 ? 'bg-rose-400' : 'bg-emerald-400'}`}
                    style={{ width: `${Math.min(100, (item.ordered_qty / item.order_qty) * 100)}%` }} />
                </div>
              )}
            </>
          )}
        </div>
        <div className="px-2 py-2 text-[10px] text-slate-500 text-center border-r border-slate-100">
          {item.uom_order}
          {item.hsn_code && <div className="text-[9px] text-slate-400">HSN {item.hsn_code}</div>}
        </div>
        <div className="px-2 py-2 text-[10px] text-slate-700 text-right border-r border-slate-100">
          {item.basic_rate ? `₹${fmt(item.basic_rate)}` : '—'}
          {item.gst_pct ? <div className="text-[9px] text-slate-400">+{item.gst_pct}% GST</div> : null}
        </div>
        <div className="px-2 py-2 text-[10px] text-slate-700 text-right border-r border-slate-100">
          {item.amount ? `₹${fmt(item.amount)}` : '—'}
        </div>
        <div className="px-2 py-2 text-[10px] font-display font-semibold text-slate-800 text-right border-r border-slate-100">
          {item.total ? `₹${fmt(item.total)}` : '—'}
        </div>
        <div className="px-1 py-2 flex flex-col items-center gap-1">
          <span className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-display font-bold border ${PASS_COLOR[ps]}`}
            title={PASS_LABEL[ps]}>
            {PASS_ICON[ps]}
          </span>
          {!isApproved && (
            <button onClick={onEdit}
              className="opacity-0 group-hover:opacity-100 p-0.5 text-slate-400 hover:text-[#3b4872]"
              data-testid={`pp-pms-edit-${item.sr_no}`} title="Edit">
              <Edit3 size={10} />
            </button>
          )}
          {!isApproved && item.ordered_qty === 0 && (
            <button onClick={onDelete}
              className="opacity-0 group-hover:opacity-100 p-0.5 text-slate-400 hover:text-rose-500"
              data-testid={`pp-pms-delete-${item.sr_no}`} title="Delete row">
              <Trash2 size={10} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


/* ── Match Review Panel ───────────────────────────────────────────────── */
function MatchReviewPanel({
  planId, matchSummary, runningMatch, showMatchPanel,
  setShowMatchPanel, onRunMatching, onConfirmAllAuto, onActionDone
}) {
  const counts = matchSummary?.counts || {};
  const unresolved = matchSummary?.unresolved_items || [];
  const allResolved = matchSummary?.all_resolved ?? false;
  const total = matchSummary?.total_items || 0;
  const resolvedCount = total - unresolved.length;
  const pct = total ? Math.round((resolvedCount / total) * 100) : 0;
  const autoCount = counts.matched || 0;
  const reviewCount = counts.review || 0;
  const unmatchedCount = counts.unmatched || 0;
  const pendingCount = counts.pending || 0;
  const confirmedCount = (counts.confirmed || 0) + (counts.overridden || 0) + (counts.new_master || 0) + (counts.deferred || 0);

  const hasUnresolved = (pendingCount + reviewCount + unmatchedCount) > 0;
  const hasAutoToConfirm = autoCount > 0;

  return (
    <div className={`bg-white border rounded-sm ${allResolved ? 'border-emerald-200' : hasUnresolved ? 'border-amber-300' : 'border-slate-200'}`}
      data-testid="pp-pms-match-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <Link size={14} className={allResolved ? 'text-emerald-600' : 'text-amber-600'} />
          <span className="text-xs font-display font-bold text-[#3b4872] uppercase tracking-widest">
            Material Master Matching
          </span>
          {allResolved
            ? <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm text-[10px] font-display font-bold" data-testid="pp-pms-match-complete">ALL RESOLVED</span>
            : hasUnresolved && <span className="px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-sm text-[10px] font-display font-bold" data-testid="pp-pms-match-pending">{unresolved.length} NEED REVIEW</span>
          }
        </div>
        <div className="flex items-center gap-2">
          {hasAutoToConfirm && (
            <button onClick={onConfirmAllAuto}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-display font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded-sm hover:bg-sky-100"
              data-testid="pp-pms-confirm-all-auto">
              <CheckCheck size={11} /> Confirm {autoCount} Auto-matched
            </button>
          )}
          <button onClick={onRunMatching} disabled={runningMatch}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-display font-bold text-[#3b4872] bg-indigo-50 border border-indigo-200 rounded-sm hover:bg-indigo-100 disabled:opacity-50"
            data-testid="pp-pms-run-matching">
            {runningMatch ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />}
            {runningMatch ? 'Matching…' : 'Run Auto-Match'}
          </button>
          {unresolved.length > 0 && (
            <button onClick={() => setShowMatchPanel(p => !p)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-display font-bold text-slate-600 bg-white border border-slate-300 rounded-sm hover:bg-slate-50"
              data-testid="pp-pms-toggle-review">
              {showMatchPanel ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
              Review Items
            </button>
          )}
        </div>
      </div>

      {/* Progress bar + status counts */}
      <div className="px-4 py-3">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${allResolved ? 'bg-emerald-500' : 'bg-indigo-500'}`}
              style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[10px] font-display font-bold text-slate-600">{pct}% resolved ({resolvedCount}/{total})</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'confirmed', label: `${confirmedCount} confirmed`, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
            { key: 'matched', label: `${autoCount} auto-matched`, color: 'bg-sky-50 text-sky-700 border-sky-200' },
            { key: 'review', label: `${reviewCount} need review`, color: 'bg-amber-50 text-amber-700 border-amber-200' },
            { key: 'unmatched', label: `${unmatchedCount} unmatched`, color: 'bg-rose-50 text-rose-700 border-rose-200' },
            { key: 'pending', label: `${pendingCount} not run`, color: 'bg-slate-100 text-slate-500 border-slate-200' },
          ].filter(b => {
            if (b.key === 'confirmed') return confirmedCount > 0;
            if (b.key === 'pending') return pendingCount > 0;
            return (counts[b.key] || 0) > 0;
          }).map(b => (
            <span key={b.key} className={`px-2 py-0.5 rounded-sm text-[10px] font-display font-semibold border ${b.color}`}>{b.label}</span>
          ))}
        </div>
        {!allResolved && !matchSummary?.last_match_run_at && (
          <div className="mt-2 text-[10px] text-amber-700 flex items-center gap-1">
            <AlertTriangle size={10} /> Run auto-match first, then confirm or resolve remaining items before Planner approval
          </div>
        )}
      </div>

      {/* Expandable review table */}
      {showMatchPanel && unresolved.length > 0 && (
        <div className="border-t border-slate-200" data-testid="pp-pms-review-table">
          <div className="px-4 py-2 bg-slate-50 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">
            Items needing resolution ({unresolved.length})
          </div>
          {unresolved.map(item => (
            <MatchItemRow key={item.id} item={item} planId={planId} onDone={onActionDone} />
          ))}
        </div>
      )}
    </div>
  );
}


/* ── Match Item Row ────────────────────────────────────────────────────── */
function MatchItemRow({ item, planId, onDone }) {
  const [selectedMasterId, setSelectedMasterId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [acting, setActing] = useState(false);
  const [showNewMaster, setShowNewMaster] = useState(false);
  const [newMasterName, setNewMasterName] = useState(item.name || '');
  const [newMasterUOM, setNewMasterUOM] = useState(item.uom_order || '');
  const [newMasterHSN, setNewMasterHSN] = useState('');

  const searchMaster = async (q) => {
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const data = await apiGet('/material-master/search', { q, limit: 5 });
      setSearchResults(data.materials || []);
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  };

  const doAction = async (action, extra = {}) => {
    setActing(true);
    try {
      await apiPost(`/project-planning/plans/${planId}/material-sheet/items/${item.id}/match`, {
        action, ...extra,
      });
      toast.success(action === 'defer' ? 'Marked as N/A' : action === 'new_master' ? 'New master created' : 'Match confirmed');
      onDone();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Action failed');
    } finally { setActing(false); }
  };

  const status = item.match_status || 'pending';
  const candidates = item.match_candidates || [];
  const effectiveSelected = selectedMasterId || (status === 'matched' ? item.matched_master_id : null);

  return (
    <div className={`px-4 py-3 border-b border-slate-100 ${status === 'unmatched' ? 'bg-rose-50/30' : status === 'review' ? 'bg-amber-50/30' : ''}`}
      data-testid={`pp-pms-match-item-${item.sr_no}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        {/* Item info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[10px] text-slate-400 font-display">#{item.sr_no}</span>
            <span className="text-xs font-display font-semibold text-slate-800">{item.name}</span>
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-display font-bold border ${MATCH_STATUS_COLOR[status]}`}>
              {MATCH_STATUS_LABEL[status]}
              {item.match_score > 0 && ` ${item.match_score}%`}
            </span>
          </div>
          {item.spec && <div className="text-[10px] text-slate-500 mb-1">{item.spec}</div>}

          {/* Auto-match display */}
          {status === 'matched' && item.matched_master_name && (
            <div className="flex items-center gap-1.5 text-[10px] text-sky-700 bg-sky-50 border border-sky-200 rounded px-2 py-1 mb-2 w-fit">
              <CheckCircle2 size={10} />
              Auto-matched → <strong>{item.matched_master_code}</strong> {item.matched_master_name}
              <span className="text-sky-400">({item.match_score}%)</span>
            </div>
          )}

          {/* Candidates (review state) */}
          {candidates.length > 0 && status !== 'matched' && (
            <div className="mb-2 space-y-1">
              <div className="text-[10px] text-slate-500 font-display font-semibold">Suggestions:</div>
              {candidates.map(c => (
                <label key={c.id} className={`flex items-center gap-2 text-[10px] cursor-pointer px-2 py-1 rounded border ${effectiveSelected === c.id ? 'bg-indigo-50 border-indigo-300 text-indigo-800' : 'border-slate-100 hover:border-slate-300'}`}>
                  <input type="radio" name={`match-${item.id}`} value={c.id}
                    checked={effectiveSelected === c.id}
                    onChange={() => setSelectedMasterId(c.id)}
                    className="accent-indigo-600" />
                  <span className="font-display font-semibold">{c.material_id}</span>
                  <span>{c.name} {c.variant}</span>
                  <span className="ml-auto text-slate-400">{c.score}%</span>
                </label>
              ))}
            </div>
          )}

          {/* Search override */}
          <div className="flex items-center gap-2 mb-2">
            <div className="relative flex-1 max-w-xs">
              <Search size={10} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); searchMaster(e.target.value); }}
                placeholder="Search Material Master…"
                className="w-full pl-6 pr-3 py-1 text-[10px] border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-300"
                data-testid={`pp-pms-search-${item.sr_no}`}
              />
            </div>
            {searching && <Loader2 size={10} className="animate-spin text-slate-400" />}
          </div>
          {searchResults.length > 0 && (
            <div className="mb-2 space-y-1 max-h-32 overflow-y-auto">
              {searchResults.map(r => (
                <label key={r.id} className={`flex items-center gap-2 text-[10px] cursor-pointer px-2 py-1 rounded border ${selectedMasterId === r.id ? 'bg-indigo-50 border-indigo-300 text-indigo-800' : 'border-slate-100 hover:border-slate-300'}`}>
                  <input type="radio" name={`match-${item.id}`} value={r.id}
                    checked={selectedMasterId === r.id}
                    onChange={() => setSelectedMasterId(r.id)}
                    className="accent-indigo-600" />
                  <span className="font-display font-semibold">{r.material_id}</span>
                  <span>{r.name}</span>
                </label>
              ))}
            </div>
          )}

          {/* New master inline form */}
          {showNewMaster && (
            <div className="bg-violet-50 border border-violet-200 rounded p-3 mb-2 space-y-2" data-testid={`pp-pms-new-master-form-${item.sr_no}`}>
              <div className="text-[10px] font-display font-bold text-violet-700 uppercase tracking-widest">Create New Material Master</div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-3">
                  <label className="text-[9px] text-violet-600 font-display font-semibold uppercase block mb-0.5">Material Name *</label>
                  <input value={newMasterName} onChange={e => setNewMasterName(e.target.value)}
                    className="w-full border border-violet-200 rounded px-2 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-violet-300"
                    data-testid={`pp-pms-new-name-${item.sr_no}`} />
                </div>
                <div>
                  <label className="text-[9px] text-violet-600 font-display font-semibold uppercase block mb-0.5">UOM *</label>
                  <input value={newMasterUOM} onChange={e => setNewMasterUOM(e.target.value)}
                    className="w-full border border-violet-200 rounded px-2 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-violet-300"
                    data-testid={`pp-pms-new-uom-${item.sr_no}`} />
                </div>
                <div>
                  <label className="text-[9px] text-violet-600 font-display font-semibold uppercase block mb-0.5">HSN Code</label>
                  <input value={newMasterHSN} onChange={e => setNewMasterHSN(e.target.value)}
                    placeholder="e.g. 25230000"
                    className="w-full border border-violet-200 rounded px-2 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-violet-300"
                    data-testid={`pp-pms-new-hsn-${item.sr_no}`} />
                </div>
                <div className="flex items-end">
                  <button onClick={() => doAction('new_master', { name: newMasterName, uom: newMasterUOM, hsn_code: newMasterHSN })}
                    disabled={!newMasterName || !newMasterUOM || acting}
                    className="px-3 py-1 text-[10px] font-display font-bold bg-violet-600 hover:bg-violet-700 text-white rounded disabled:opacity-50"
                    data-testid={`pp-pms-create-master-${item.sr_no}`}>
                    {acting ? <Loader2 size={10} className="animate-spin" /> : 'Create'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-1.5 min-w-fit" data-testid={`pp-pms-match-actions-${item.sr_no}`}>
          {/* Confirm auto-match */}
          {status === 'matched' && (
            <button onClick={() => doAction('confirm')} disabled={acting}
              className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-display font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm disabled:opacity-50 whitespace-nowrap"
              data-testid={`pp-pms-confirm-${item.sr_no}`}>
              <CheckCircle2 size={10} /> Confirm
            </button>
          )}

          {/* Confirm selected candidate or search result */}
          {effectiveSelected && status !== 'matched' && (
            <button onClick={() => doAction('override', { master_id: effectiveSelected })} disabled={acting}
              className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-display font-bold bg-indigo-600 hover:bg-indigo-700 text-white rounded-sm disabled:opacity-50 whitespace-nowrap"
              data-testid={`pp-pms-override-${item.sr_no}`}>
              <Link size={10} /> Use This
            </button>
          )}

          {/* Create new master */}
          <button onClick={() => setShowNewMaster(p => !p)}
            className={`flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-display font-bold rounded-sm whitespace-nowrap ${showNewMaster ? 'bg-violet-100 text-violet-700 border border-violet-300' : 'bg-white text-slate-600 border border-slate-300 hover:border-violet-400 hover:text-violet-700'}`}
            data-testid={`pp-pms-new-master-${item.sr_no}`}>
            <PlusCircle size={10} /> New Master
          </button>

          {/* Defer */}
          <button onClick={() => doAction('defer')} disabled={acting}
            className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-display font-bold text-slate-500 bg-slate-50 border border-slate-200 rounded-sm hover:bg-slate-100 whitespace-nowrap"
            data-testid={`pp-pms-defer-${item.sr_no}`}>
            <SkipForward size={10} /> Defer / N/A
          </button>
        </div>
      </div>
    </div>
  );
}


/* ── Edit modal (fully editable) ────────────────────────────────── */
function EditModal({ item, setItem, onSave, onClose }) {
  const F = ({ label, children }) => (
    <div>
      <label className="block text-[10px] uppercase font-display font-bold text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
  const inp = "w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#3b4872]";
  const sel = "w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#3b4872] bg-white";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="pp-pms-edit-modal">
      <div className="bg-white rounded-sm w-full max-w-2xl shadow-xl border border-slate-200 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-shrink-0">
          <span className="text-sm font-display font-bold text-[#3b4872]">Edit Material Item</span>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-4 text-sm overflow-y-auto flex-1">
          {/* Row 1 */}
          <F label="Material Name">
            <input value={item.name || ''} onChange={e => setItem(p => ({ ...p, name: e.target.value }))}
              className={inp} data-testid="pms-edit-name" />
          </F>
          <F label="Specification">
            <input value={item.spec || ''} onChange={e => setItem(p => ({ ...p, spec: e.target.value }))}
              className={inp} data-testid="pms-edit-spec" />
          </F>
          {/* Row 2 */}
          <div className="grid grid-cols-3 gap-3">
            <F label="Pass / Source Type">
              <select value={item.pass_source || 1} onChange={e => setItem(p => ({ ...p, pass_source: parseInt(e.target.value) }))}
                className={sel} data-testid="pms-edit-pass-source">
                <option value={1}>1 — BOQ Purchasable</option>
                <option value={2}>2 — Derived (Norms)</option>
                <option value={3}>3 — Consumable</option>
                <option value={4}>4 — Alternative</option>
              </select>
            </F>
            <F label="Material Type">
              <input value={item.material_type || ''} onChange={e => setItem(p => ({ ...p, material_type: e.target.value }))}
                placeholder="e.g. Binder, Steel, Aggregate"
                className={inp} data-testid="pms-edit-material-type" />
            </F>
            <F label="HSN Code">
              <input value={item.hsn_code || ''} onChange={e => setItem(p => ({ ...p, hsn_code: e.target.value }))}
                placeholder="e.g. 2523"
                className={inp} data-testid="pms-edit-hsn" />
            </F>
          </div>
          {/* Row 3 — Quantities */}
          <div className="grid grid-cols-4 gap-3">
            <F label="BOQ Qty">
              <input type="number" value={item.boq_qty ?? ''} onChange={e => setItem(p => ({ ...p, boq_qty: parseFloat(e.target.value) || 0 }))}
                className={inp} data-testid="pms-edit-boq-qty" />
            </F>
            <F label="UOM (BOQ)">
              <input value={item.uom_boq || ''} onChange={e => setItem(p => ({ ...p, uom_boq: e.target.value }))}
                placeholder="e.g. Cum, Sqm"
                className={inp} data-testid="pms-edit-uom-boq" />
            </F>
            <F label="Order Cap Qty (MAX)">
              <input type="number" value={item.order_qty ?? ''} onChange={e => setItem(p => ({ ...p, order_qty: parseFloat(e.target.value) || 0 }))}
                className={inp} data-testid="pms-edit-order-qty" />
            </F>
            <F label="UOM (Order)">
              <input value={item.uom_order || ''} onChange={e => setItem(p => ({ ...p, uom_order: e.target.value }))}
                placeholder="e.g. MT, Bags, Nos"
                className={inp} data-testid="pms-edit-uom-order" />
            </F>
          </div>
          {/* Row 4 — Rates */}
          <div className="grid grid-cols-3 gap-3">
            <F label="Basic Rate (₹)">
              <input type="number" value={item.basic_rate ?? ''} onChange={e => setItem(p => ({ ...p, basic_rate: parseFloat(e.target.value) || 0 }))}
                className={inp} data-testid="pms-edit-rate" />
            </F>
            <F label="GST %">
              <input type="number" value={item.gst_pct ?? ''} onChange={e => setItem(p => ({ ...p, gst_pct: parseFloat(e.target.value) || 0 }))}
                placeholder="18"
                className={inp} data-testid="pms-edit-gst" />
            </F>
            <F label="Alternative Group ID">
              <input value={item.alternative_group_id || ''} onChange={e => setItem(p => ({ ...p, alternative_group_id: e.target.value }))}
                placeholder="e.g. GRP-A (blank if none)"
                className={inp} data-testid="pms-edit-alt-group" />
            </F>
          </div>
          {/* Row 5 — Refs */}
          <div className="grid grid-cols-2 gap-3">
            <F label="BOQ Reference">
              <input value={item.boq_ref || ''} onChange={e => setItem(p => ({ ...p, boq_ref: e.target.value }))}
                placeholder="BOQ item code(s) this derives from"
                className={inp} data-testid="pms-edit-boq-ref" />
            </F>
            <F label="WBS Reference">
              <input value={item.wbs_ref || ''} onChange={e => setItem(p => ({ ...p, wbs_ref: e.target.value }))}
                placeholder="WBS package code"
                className={inp} data-testid="pms-edit-wbs-ref" />
            </F>
          </div>
          {/* Row 6 — Text fields */}
          <F label="Calculation Basis / Norms">
            <textarea value={item.basis || ''} onChange={e => setItem(p => ({ ...p, basis: e.target.value }))} rows={3}
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#3b4872]"
              data-testid="pms-edit-basis" />
          </F>
          <F label="Brand Options (one per line)">
            <textarea
              value={(item.brand_options || []).join('\n')}
              onChange={e => setItem(p => ({ ...p, brand_options: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) }))}
              rows={2}
              placeholder="UltraTech&#10;ACC&#10;Ambuja"
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#3b4872]"
              data-testid="pms-edit-brands" />
          </F>
          {/* Client-supplied toggle */}
          <div className="flex items-center gap-3 pt-1">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox"
                checked={!!item.client_supplied}
                onChange={e => setItem(p => ({ ...p, client_supplied: e.target.checked }))}
                className="w-4 h-4 accent-sky-600"
                data-testid="pms-edit-client-supplied" />
              <span className="text-sm text-slate-700 font-semibold">Client-supplied</span>
              <span className="text-xs text-slate-500">(exempt from procurement cap enforcement)</span>
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 bg-slate-50 border border-slate-300 rounded-sm hover:bg-slate-100">
            Cancel
          </button>
          <button onClick={onSave}
            className="px-4 py-2 text-sm font-display font-bold bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm"
            data-testid="pms-edit-save-btn">
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}


/* ── BOQ Gap Panel ────────────────────────────────────────────────── */
function BoqGapPanel({ gaps }) {
  const [expanded, setExpanded] = useState(false);
  if (!gaps?.length) return null;
  return (
    <div className="bg-amber-50 border border-amber-300 rounded-sm" data-testid="pp-pms-boq-gap-panel">
      <button onClick={() => setExpanded(p => !p)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-amber-100"
        data-testid="pp-pms-boq-gap-toggle">
        <AlertTriangle size={14} className="text-amber-600 flex-shrink-0" />
        <span className="text-xs font-display font-bold text-amber-800">
          BOQ Gap: {gaps.length} composite BOQ item{gaps.length > 1 ? 's' : ''} have no derived material rows
        </span>
        <span className="ml-auto text-[10px] text-amber-600 flex items-center gap-1">
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {expanded ? 'Hide' : 'View'}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-amber-200 px-4 py-3 space-y-2">
          <p className="text-[10px] text-amber-700 mb-2">
            These BOQ line items (Concrete, Masonry, Plaster, etc.) should produce derived material rows
            (Cement, Sand, Aggregates) but none were generated. Review the Material Sheet and check the
            generation prompt or add rows manually before approval.
          </p>
          {gaps.map((g, i) => (
            <div key={i} className="flex items-start gap-2 text-[10px] text-amber-800 bg-amber-100 rounded px-2 py-1.5">
              <span className="font-display font-bold text-amber-600 mt-0.5">#{g.code || i + 1}</span>
              <div>
                <span className="font-semibold">{g.description}</span>
                <span className="text-amber-600 ml-2">{g.quantity} {g.unit}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ── Audit Trail Section ───────────────────────────────────────────── */
function AuditTrailSection({ sheet, items, show, onToggle }) {
  if (!sheet) return null;
  const approvals = sheet.approvals || [];
  const sheetRevisions = sheet.revision_log || [];

  // Collect item-level events (match actions + order_qty revisions)
  const itemEvents = [];
  for (const it of items) {
    for (const rev of (it.revision_log || [])) {
      itemEvents.push({ ...rev, item_name: it.name, sr_no: it.sr_no, _type: 'revision' });
    }
    if (it.match_action_by) {
      itemEvents.push({
        at: it.match_action_at,
        by: it.match_action_by,
        item_name: it.name,
        sr_no: it.sr_no,
        match_status: it.match_status,
        matched_master_code: it.matched_master_code,
        _type: 'match',
      });
    }
  }
  // Sort all events by timestamp desc
  const allEvents = [
    ...approvals.map(e => ({ ...e, _type: 'approval' })),
    ...sheetRevisions.map(e => ({ ...e, _type: 'sheet_revision' })),
    ...itemEvents,
  ].sort((a, b) => (b.at || '').localeCompare(a.at || ''));

  const formatAt = (at) => {
    if (!at) return '—';
    try {
      return formatDateTime(at);
    } catch { return at?.slice(0, 16).replace('T', ' '); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="pp-pms-audit-section">
      <button onClick={onToggle}
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-slate-50 text-left"
        data-testid="pp-pms-audit-toggle">
        <History size={14} className="text-slate-500" />
        <span className="text-xs font-display font-bold uppercase tracking-widest text-slate-500">
          Audit Trail
        </span>
        <span className="ml-1 px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded text-[10px]">{allEvents.length}</span>
        <span className="ml-auto text-[10px] text-slate-400 flex items-center gap-1">
          {show ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {show ? 'Collapse' : 'Expand'}
        </span>
      </button>
      {show && (
        <div className="border-t border-slate-200 divide-y divide-slate-100 max-h-96 overflow-y-auto"
          data-testid="pp-pms-audit-list">
          {allEvents.length === 0
            ? <div className="px-4 py-4 text-xs text-slate-400 text-center">No audit events yet</div>
            : allEvents.map((ev, i) => (
              <div key={i} className="flex items-start gap-3 px-4 py-2.5 hover:bg-slate-50"
                data-testid={`pp-pms-audit-event-${i}`}>
                <div className={`mt-0.5 flex-shrink-0 w-1.5 h-1.5 rounded-full mt-1.5 ${
                  ev._type === 'approval' ? 'bg-emerald-500' :
                  ev._type === 'match' ? 'bg-sky-500' :
                  ev._type === 'revision' ? 'bg-amber-500' : 'bg-slate-400'
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-[10px] font-display font-semibold text-slate-700">
                      {ev._type === 'approval'
                        ? `${ev.action === 'approve' ? '✓ Approved' : '✗ Rejected'} (${(ev.from || '').replace(/_/g, ' ')} → ${(ev.to || '').replace(/_/g, ' ')})`
                        : ev._type === 'match'
                        ? `#${ev.sr_no} ${ev.item_name?.slice(0, 35)} — ${(MATCH_STATUS_LABEL[ev.match_status] || ev.match_status)}${ev.matched_master_code ? ` → ${ev.matched_master_code}` : ''}`
                        : ev._type === 'revision'
                        ? `#${ev.sr_no} ${ev.item_name?.slice(0, 35)} — order qty revised`
                        : `Sheet revised · ${ev.items_edited || 0} items`
                      }
                    </span>
                    <span className="text-[9px] text-slate-400 whitespace-nowrap">{formatAt(ev.at)}</span>
                  </div>
                  <div className="text-[10px] text-slate-500">{ev.by}</div>
                  {ev.reason && <div className="text-[10px] text-rose-600 italic">{ev.reason}</div>}
                </div>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}



/* ── Generation Input Wizard (Pre-generation setup) ──────────────── */
function GenerationInputWizard({ existingInputs, onGenerate, onClose, hasExistingSheet }) {
  const GRADE_OPTIONS = ['M15', 'M20', 'M25', 'M30', 'M35', 'M40'];
  const [concreteGrades, setConcreteGrades] = useState(existingInputs?.concrete_grades || ['M20', 'M25', 'M30']);
  const [mixDesign, setMixDesign] = useState(existingInputs?.mix_design || 'standard_is');
  const [mixCustom, setMixCustom] = useState(existingInputs?.mix_design_ratios || '');
  const [clientSupplied, setClientSupplied] = useState((existingInputs?.client_supplied_items || []).join('\n'));
  const [specialInstructions, setSpecialInstructions] = useState(existingInputs?.special_instructions || '');
  const [wastageOverrides, setWastageOverrides] = useState(
    Object.entries(existingInputs?.wastage_overrides || {}).map(([k, v]) => `${k}: ${v}`).join('\n')
  );

  const toggleGrade = (g) => setConcreteGrades(prev =>
    prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]
  );

  const parseWastageOverrides = () => {
    const result = {};
    for (const line of wastageOverrides.split('\n')) {
      const m = line.match(/^(.+?):\s*(\d+(\.\d+)?)$/);
      if (m) result[m[1].trim()] = parseFloat(m[2]);
    }
    return result;
  };

  const handleGenerate = () => {
    const inputs = {
      concrete_grades: concreteGrades,
      mix_design: mixDesign,
      mix_design_ratios: mixDesign === 'custom' ? mixCustom : '',
      client_supplied_items: clientSupplied.split('\n').map(s => s.trim()).filter(Boolean),
      special_instructions: specialInstructions.trim(),
      wastage_overrides: parseWastageOverrides(),
    };
    onGenerate(inputs);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="pp-pms-gen-wizard">
      <div className="bg-white rounded-sm w-full max-w-xl shadow-xl border border-slate-200 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Settings2 size={15} className="text-[#3b4872]" />
            <span className="text-sm font-display font-bold text-[#3b4872]">
              {hasExistingSheet ? 'Regenerate — Update Project Parameters' : 'Set Up Material Sheet Generation'}
            </span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-5 overflow-y-auto flex-1">
          <div className="text-xs text-slate-500 bg-indigo-50 border border-indigo-100 rounded px-3 py-2">
            Ellora AI uses these inputs to generate material quantities without assumptions.
            The more you provide, the more accurate the sheet will be.
          </div>

          {/* Concrete Grades */}
          <div>
            <label className="block text-xs font-display font-bold text-slate-700 mb-2">
              Concrete Grades Required <span className="text-rose-500">*</span>
              <span className="ml-1 text-[10px] text-slate-400 font-normal">(select all that apply)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {GRADE_OPTIONS.map(g => (
                <button key={g} onClick={() => toggleGrade(g)}
                  className={`px-3 py-1.5 text-xs font-display font-bold rounded border transition-colors ${
                    concreteGrades.includes(g)
                      ? 'bg-[#3b4872] text-white border-[#3b4872]'
                      : 'bg-white text-slate-500 border-slate-300 hover:border-[#3b4872]'
                  }`}
                  data-testid={`pp-pms-grade-${g}`}>
                  {g}
                </button>
              ))}
            </div>
            {concreteGrades.length === 0 && (
              <p className="text-[10px] text-amber-600 mt-1">Select at least one grade or AI will use M20/M25/M30 defaults</p>
            )}
          </div>

          {/* Mix Design */}
          <div>
            <label className="block text-xs font-display font-bold text-slate-700 mb-2">Mix Design Approach</label>
            <div className="flex gap-3">
              {[
                { v: 'standard_is', label: 'Standard IS Norms', sub: 'Use IS 456 norms (recommended)' },
                { v: 'custom', label: 'Custom Mix Design', sub: 'Enter specific ratios below' },
              ].map(opt => (
                <label key={opt.v} className={`flex-1 flex items-start gap-2 border rounded p-2.5 cursor-pointer ${mixDesign === opt.v ? 'bg-indigo-50 border-indigo-300' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input type="radio" name="mix_design" value={opt.v} checked={mixDesign === opt.v}
                    onChange={() => setMixDesign(opt.v)} className="mt-0.5 accent-indigo-600" />
                  <div>
                    <div className="text-xs font-display font-semibold text-slate-700">{opt.label}</div>
                    <div className="text-[10px] text-slate-400">{opt.sub}</div>
                  </div>
                </label>
              ))}
            </div>
            {mixDesign === 'custom' && (
              <textarea
                value={mixCustom}
                onChange={e => setMixCustom(e.target.value)}
                rows={3}
                placeholder="e.g. M30: 380 OPC + 70 FA + 0.44 CS + 0.60 CA20 + 4 PCE per cum&#10;M25: 315 OPC + 65 FA + ..."
                className="mt-2 w-full border border-indigo-300 rounded-sm px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400"
                data-testid="pp-pms-mix-custom" />
            )}
          </div>

          {/* Client-supplied materials */}
          <div>
            <label className="block text-xs font-display font-bold text-slate-700 mb-1">
              Client-Supplied Materials
              <span className="ml-1 text-[10px] text-slate-400 font-normal">(one per line — these will be excluded from procurement caps)</span>
            </label>
            <textarea
              value={clientSupplied}
              onChange={e => setClientSupplied(e.target.value)}
              rows={3}
              placeholder="e.g. Cement OPC 53 Grade&#10;TMT Steel Fe550&#10;Diesel for machinery"
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#3b4872]"
              data-testid="pp-pms-client-supplied-input" />
          </div>

          {/* Wastage overrides */}
          <div>
            <label className="block text-xs font-display font-bold text-slate-700 mb-1">
              Wastage % Overrides
              <span className="ml-1 text-[10px] text-slate-400 font-normal">(optional — format: material: percentage)</span>
            </label>
            <textarea
              value={wastageOverrides}
              onChange={e => setWastageOverrides(e.target.value)}
              rows={2}
              placeholder="cement: 5&#10;rebar: 2&#10;tiles: 8"
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#3b4872]"
              data-testid="pp-pms-wastage-overrides" />
          </div>

          {/* Special instructions */}
          <div>
            <label className="block text-xs font-display font-bold text-slate-700 mb-1">
              Special Instructions for Ellora AI
              <span className="ml-1 text-[10px] text-slate-400 font-normal">(site conditions, exclusions, specific notes)</span>
            </label>
            <textarea
              value={specialInstructions}
              onChange={e => setSpecialInstructions(e.target.value)}
              rows={2}
              placeholder="e.g. No fly ash in foundation PCC. Use M35 for columns above plinth. Rebar grades Fe500D for beams."
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#3b4872]"
              data-testid="pp-pms-special-instructions" />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 bg-slate-50 border border-slate-300 rounded-sm hover:bg-slate-100">
            Cancel
          </button>
          <button onClick={handleGenerate}
            className="flex items-center gap-2 px-5 py-2 text-sm font-display font-bold bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm"
            data-testid="pp-pms-wizard-generate-btn">
            <Sparkles size={13} />
            {hasExistingSheet ? 'Regenerate Sheet' : 'Generate Material Sheet'}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ── Add Item Modal ─────────────────────────────────────────────── */
function AddItemModal({ section, onSave, onClose }) {
  const [item, setItem] = useState({
    name: '', spec: '', pass_source: 1, material_type: '',
    section: section || 'Z. OTHER', section_order: 99,
    uom_boq: '', uom_order: '', boq_qty: 0, order_qty: 0,
    basic_rate: 0, gst_pct: 18, hsn_code: '', basis: '',
    brand_options: [], boq_ref: '', wbs_ref: '',
    client_supplied: false, alternative_group_id: '',
  });
  const [saving, setSaving] = useState(false);
  const inp = "w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#3b4872]";

  const handleSave = async () => {
    if (!item.name.trim()) { toast.error('Material name is required'); return; }
    setSaving(true);
    try { await onSave(item); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="pp-pms-add-modal">
      <div className="bg-white rounded-sm w-full max-w-xl shadow-xl border border-slate-200 flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Plus size={14} className="text-indigo-600" />
            <span className="text-sm font-display font-bold text-[#3b4872]">Add Material Row</span>
            <span className="text-[10px] text-slate-400">in {section}</span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <div>
            <label className="block text-[10px] uppercase font-display font-bold text-slate-500 mb-1">Material Name *</label>
            <input value={item.name} onChange={e => setItem(p => ({ ...p, name: e.target.value }))}
              className={inp} placeholder="e.g. Cement OPC 53 Grade"
              data-testid="pp-pms-add-name" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase font-display font-bold text-slate-500 mb-1">Pass Source</label>
              <select value={item.pass_source} onChange={e => setItem(p => ({ ...p, pass_source: parseInt(e.target.value) }))}
                className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#3b4872]"
                data-testid="pp-pms-add-pass-source">
                <option value={1}>1 — BOQ Purchasable</option>
                <option value={2}>2 — Derived (Norms)</option>
                <option value={3}>3 — Consumable</option>
                <option value={4}>4 — Alternative</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase font-display font-bold text-slate-500 mb-1">Specification</label>
              <input value={item.spec} onChange={e => setItem(p => ({ ...p, spec: e.target.value }))}
                className={inp} placeholder="Grade, standard, etc." data-testid="pp-pms-add-spec" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] uppercase font-display font-bold text-slate-500 mb-1">Order Qty (MAX)</label>
              <input type="number" value={item.order_qty || ''} onChange={e => setItem(p => ({ ...p, order_qty: parseFloat(e.target.value) || 0 }))}
                className={inp} data-testid="pp-pms-add-order-qty" />
            </div>
            <div>
              <label className="block text-[10px] uppercase font-display font-bold text-slate-500 mb-1">UOM (Order)</label>
              <input value={item.uom_order} onChange={e => setItem(p => ({ ...p, uom_order: e.target.value }))}
                className={inp} placeholder="MT / Bags / Nos" data-testid="pp-pms-add-uom-order" />
            </div>
            <div>
              <label className="block text-[10px] uppercase font-display font-bold text-slate-500 mb-1">Basic Rate (₹)</label>
              <input type="number" value={item.basic_rate || ''} onChange={e => setItem(p => ({ ...p, basic_rate: parseFloat(e.target.value) || 0 }))}
                className={inp} data-testid="pp-pms-add-rate" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase font-display font-bold text-slate-500 mb-1">Basis / Notes</label>
            <textarea value={item.basis} onChange={e => setItem(p => ({ ...p, basis: e.target.value }))} rows={2}
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#3b4872]"
              data-testid="pp-pms-add-basis" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!item.client_supplied}
              onChange={e => setItem(p => ({ ...p, client_supplied: e.target.checked }))}
              className="w-4 h-4 accent-sky-600" data-testid="pp-pms-add-client-supplied" />
            <span className="text-sm text-slate-700">Client-supplied (exempt from procurement cap)</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 bg-slate-50 border border-slate-300 rounded-sm hover:bg-slate-100">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm font-display font-bold bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm disabled:opacity-50"
            data-testid="pp-pms-add-save-btn">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            Add Row
          </button>
        </div>
      </div>
    </div>
  );
}


/* ── Cap Override Panel (MD-approved sheet only) ────────────────── */
function CapOverridePanel({ overrides, onApprove, onReject, show, onToggle }) {
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const pending = overrides.filter(o => o.status === 'pending');
  const others = overrides.filter(o => o.status !== 'pending');

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="pp-pms-cap-overrides">
      <button onClick={onToggle}
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-slate-50 text-left"
        data-testid="pp-pms-cap-overrides-toggle">
        <ArrowUpRight size={14} className={pending.length > 0 ? 'text-amber-600' : 'text-slate-400'} />
        <span className="text-xs font-display font-bold uppercase tracking-widest text-slate-500">
          Cap Override Requests
        </span>
        {pending.length > 0 && (
          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 border border-amber-200 rounded text-[10px] font-display font-bold">
            {pending.length} pending
          </span>
        )}
        <span className="ml-auto text-[10px] text-slate-400 flex items-center gap-1">
          {show ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </span>
      </button>
      {show && (
        <div className="border-t border-slate-200 divide-y divide-slate-100">
          {overrides.length === 0 && (
            <div className="px-4 py-6 text-xs text-slate-400 text-center">
              No cap override requests yet. Hover over an approved-sheet row to request a cap change.
            </div>
          )}
          {pending.map(ov => (
            <div key={ov.id} className="px-4 py-3 bg-amber-50/50" data-testid={`pp-pms-override-${ov.id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="flex-1">
                  <div className="text-xs font-display font-semibold text-slate-800">{ov.item_name}</div>
                  <div className="text-[10px] text-amber-800 mt-0.5">
                    Change cap: <strong>{ov.current_order_qty}</strong> → <strong className="text-amber-900">{ov.new_order_qty}</strong> {ov.uom}
                  </div>
                  <div className="text-[10px] text-slate-600 mt-0.5 italic">"{ov.reason}"</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">Requested by {ov.requested_by}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => onApprove(ov.id)}
                    className="px-3 py-1.5 text-[10px] font-display font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded"
                    data-testid={`pp-pms-override-approve-${ov.id}`}>
                    Approve
                  </button>
                  <button onClick={() => { setRejectModal(ov.id); setRejectReason(''); }}
                    className="px-3 py-1.5 text-[10px] font-display font-bold bg-rose-50 border border-rose-300 text-rose-700 hover:bg-rose-100 rounded"
                    data-testid={`pp-pms-override-reject-${ov.id}`}>
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
          {others.slice(0, 5).map(ov => (
            <div key={ov.id} className="px-4 py-2.5 text-[10px] text-slate-500 flex items-center gap-3">
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-display font-bold border ${ov.status === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                {ov.status}
              </span>
              <span className="font-semibold text-slate-700">{ov.item_name}</span>
              <span>{ov.current_order_qty} → {ov.new_order_qty} {ov.uom}</span>
              <span className="ml-auto text-slate-400">{ov.approved_by || ov.rejected_by}</span>
            </div>
          ))}
        </div>
      )}

      {/* Reject reason modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm w-full max-w-sm shadow-xl border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-200 text-sm font-display font-bold text-rose-700">Reject Cap Override</div>
            <div className="p-4">
              <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3}
                placeholder="Reason for rejection…"
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-rose-400"
                data-testid="pp-pms-override-reject-reason" />
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200">
              <button onClick={() => setRejectModal(null)} className="px-3 py-2 text-sm text-slate-600 bg-slate-50 border border-slate-300 rounded">Cancel</button>
              <button onClick={() => { onReject(rejectModal, rejectReason); setRejectModal(null); }}
                className="px-3 py-2 text-sm font-bold bg-rose-600 text-white rounded hover:bg-rose-700"
                data-testid="pp-pms-override-reject-submit">
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ── Reconciliation Tab ─────────────────────────────────────────── */
function ReconciliationTab({ reconciliation, loading, onRefresh, onBackfill }) {
  const fmtN = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 }));

  const SIGNAL_COLOR = {
    not_started: 'bg-slate-100 text-slate-500',
    in_progress: 'bg-blue-50 text-blue-700',
    near_cap: 'bg-amber-50 text-amber-700',
    fully_received: 'bg-emerald-50 text-emerald-700',
    on_track: 'bg-slate-50 text-slate-500',
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-8 flex items-center justify-center gap-2" data-testid="pp-pms-recon-loading">
        <Loader2 size={16} className="animate-spin text-[#3b4872]" />
        <span className="text-sm text-slate-500">Loading reconciliation data…</span>
      </div>
    );
  }

  if (!reconciliation) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-8 text-center" data-testid="pp-pms-recon-empty">
        <BarChart2 size={32} className="text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500 mb-3">No reconciliation data yet</p>
        <button onClick={onRefresh} className="px-4 py-2 text-xs font-display font-bold bg-[#3b4872] text-white rounded-sm hover:bg-[#2d3856]"
          data-testid="pp-pms-recon-load-btn">Load Reconciliation</button>
      </div>
    );
  }

  const s = reconciliation.summary || {};

  return (
    <div className="space-y-4" data-testid="pp-pms-reconciliation">
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Items with Cap', value: s.items_with_cap || 0, sub: `of ${s.total_items || 0} total` },
          { label: 'Ordered', value: s.ordered_count || 0, sub: 'items with ordered qty' },
          { label: 'Received (GRN)', value: s.received_count || 0, sub: 'items with GRN receipts' },
          { label: 'Client-Supplied', value: s.client_supplied_count || 0, sub: 'exempt from caps' },
        ].map(k => (
          <div key={k.label} className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="text-[10px] uppercase tracking-widest font-display text-slate-500">{k.label}</div>
            <div className="text-xl font-display font-bold text-[#3b4872]">{k.value}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Backfill wizard button */}
      <div className="flex items-center justify-between bg-indigo-50/60 border border-indigo-100 rounded-sm px-4 py-2.5">
        <div className="text-xs text-indigo-700">
          <span className="font-display font-bold">Backfill wizard:</span>{' '}
          Seed <code>ordered_qty</code> from existing approved POs in the system, or upload a Megafine Excel.
        </div>
        <button onClick={onBackfill}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold text-indigo-700 bg-white border border-indigo-300 rounded hover:bg-indigo-50"
          data-testid="pp-pms-backfill-btn">
          <Upload size={11} /> Backfill Ordered Qty
        </button>
      </div>

      {/* Per-section tables */}
      {(reconciliation.sections || []).map(sec => (
        <div key={sec.section} className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="bg-slate-50 px-4 py-2 text-xs font-display font-bold text-[#3b4872] border-b border-slate-200">
            {sec.section}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[10px]">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  {['#', 'Material', 'UOM', 'Cap (MAX)', 'Ordered', 'To Order', 'Received', 'Issued', 'In Store', 'Status'].map(h => (
                    <th key={h} className="px-2 py-1.5 text-left font-display font-bold text-slate-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {(sec.items || []).map(row => (
                  <tr key={row.id} className="hover:bg-slate-50/60" data-testid={`pp-pms-recon-row-${row.sr_no}`}>
                    <td className="px-2 py-1.5 text-slate-400">{row.sr_no}</td>
                    <td className="px-2 py-1.5 max-w-[200px]">
                      <div className="font-display font-semibold text-slate-700 leading-snug truncate">{row.name}</div>
                      {row.client_supplied && (
                        <span className="text-[9px] text-sky-600 font-bold">Client-supplied</span>
                      )}
                      {row.alternative_group_id && (
                        <span className="text-[9px] text-violet-600 ml-1 font-bold">Alt:{row.alternative_group_id}</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-slate-500">{row.uom}</td>
                    <td className="px-2 py-1.5 font-display font-bold text-slate-700">{fmtN(row.cap)}</td>
                    <td className="px-2 py-1.5 text-indigo-700 font-bold">{fmtN(row.ordered)}</td>
                    <td className={`px-2 py-1.5 font-bold ${row.remaining_to_order > 0 ? 'text-amber-700' : 'text-slate-400'}`}>
                      {fmtN(row.remaining_to_order)}
                    </td>
                    <td className="px-2 py-1.5 text-emerald-700">{fmtN(row.received)}</td>
                    <td className="px-2 py-1.5 text-rose-600">{fmtN(row.issued)}</td>
                    <td className="px-2 py-1.5 text-slate-700 font-semibold">{fmtN(row.balance_in_store)}</td>
                    <td className="px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-display font-bold ${SIGNAL_COLOR[row.status] || 'bg-slate-50 text-slate-400'}`}>
                        {row.status?.replace(/_/g, ' ')}
                      </span>
                      {row.cap > 0 && (
                        <div className="mt-0.5 h-1 bg-slate-100 rounded-full overflow-hidden w-16">
                          <div className="h-full bg-indigo-400 rounded-full"
                            style={{ width: `${Math.min(100, row.util_pct)}%` }} />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div className="flex justify-end">
        <button onClick={onRefresh} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-500 bg-white border border-slate-300 rounded hover:border-[#3b4872]"
          data-testid="pp-pms-recon-refresh-btn">
          <RefreshCw size={11} /> Refresh
        </button>
      </div>
    </div>
  );
}


/* ── Gaps Forecast Tab ─────────────────────────────────────────── */
function GapsForecastTab({ forecast, loading, onRefresh }) {
  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-8 flex items-center justify-center gap-2">
        <Loader2 size={16} className="animate-spin text-[#3b4872]" />
        <span className="text-sm text-slate-500">Computing gaps forecast…</span>
      </div>
    );
  }

  if (!forecast) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
        <TrendingUp size={32} className="text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500 mb-3">No forecast data yet</p>
        {loading ? (
          <div className="flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin text-[#3b4872]" />
            <span className="text-xs text-slate-500">Loading…</span>
          </div>
        ) : (
          <button onClick={onRefresh} className="px-4 py-2 text-xs font-display font-bold bg-[#3b4872] text-white rounded-sm hover:bg-[#2d3856]">
            Load Forecast
          </button>
        )}
      </div>
    );
  }

  const { elapsed_pct, summary, items, project_name, start_date, end_date } = forecast;

  const SIGNAL_BG = { red: 'bg-rose-50 border-rose-200', amber: 'bg-amber-50 border-amber-200', green: 'bg-emerald-50 border-emerald-200' };
  const SIGNAL_TEXT = { red: 'text-rose-700', amber: 'text-amber-700', green: 'text-emerald-700' };
  const SIGNAL_DOT = { red: 'bg-rose-500', amber: 'bg-amber-500', green: 'bg-emerald-500' };

  return (
    <div className="space-y-4" data-testid="pp-pms-forecast">
      {/* Summary */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs font-display font-bold text-slate-600">{project_name}</div>
            <div className="text-[10px] text-slate-400">{start_date} → {end_date}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Timeline Elapsed</div>
            <div className="text-2xl font-display font-bold text-[#3b4872]">{elapsed_pct}%</div>
          </div>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-[#3b4872] rounded-full transition-all"
            style={{ width: `${Math.min(100, elapsed_pct)}%` }} />
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          {[
            { label: 'At Risk', count: summary?.red || 0, signal: 'red' },
            { label: 'Slightly Behind', count: summary?.amber || 0, signal: 'amber' },
            { label: 'On Track', count: summary?.green || 0, signal: 'green' },
          ].map(s => (
            <div key={s.label} className={`border rounded-sm p-3 text-center ${SIGNAL_BG[s.signal]}`}>
              <div className={`text-2xl font-display font-bold ${SIGNAL_TEXT[s.signal]}`}>{s.count}</div>
              <div className="text-[10px] font-display font-semibold mt-0.5 text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Items list */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 text-xs font-display font-bold text-slate-500">
          Material-level Ordering Status vs Schedule Progress
        </div>
        <div className="divide-y divide-slate-50">
          {(items || []).map(it => (
            <div key={it.id} className={`flex items-center gap-3 px-4 py-2.5 border-l-4 ${SIGNAL_BG[it.signal]?.split(' ')[0]} border-${it.signal === 'red' ? 'rose' : it.signal === 'amber' ? 'amber' : 'emerald'}-400`}
              data-testid={`pp-pms-forecast-row-${it.sr_no}`}>
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${SIGNAL_DOT[it.signal]}`} />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-display font-semibold text-slate-700 truncate">{it.name}</div>
                <div className="text-[9px] text-slate-400">{it.section}</div>
              </div>
              <div className="text-[10px] text-right flex-shrink-0">
                <div className="font-display font-bold text-slate-600">Ordered: {it.ordering_pct}%</div>
                <div className="text-slate-400">Expected: {it.expected_pct}%</div>
              </div>
              <div className={`text-[10px] font-display font-bold ${SIGNAL_TEXT[it.signal]} flex-shrink-0 min-w-[80px] text-right`}>
                {it.label}
              </div>
              <div className="w-20 flex-shrink-0">
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${SIGNAL_DOT[it.signal]}`}
                    style={{ width: `${Math.min(100, it.ordering_pct)}%` }} />
                </div>
              </div>
            </div>
          ))}
          {(items || []).length === 0 && (
            <div className="px-4 py-6 text-xs text-slate-400 text-center">
              No purchasable or derived items found in the PMS for forecast analysis.
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={onRefresh} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-500 bg-white border border-slate-300 rounded hover:border-[#3b4872]">
          <RefreshCw size={11} /> Refresh Forecast
        </button>
      </div>
    </div>
  );
}


/* ── Backfill Wizard ─────────────────────────────────────────────── */
function BackfillWizard({ planId, onClose, onDone }) {
  const [mode, setMode] = useState('pos');  // 'pos' | 'excel'
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committed, setCommitted] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const fileRef = useRef(null);

  const runPosBackfill = async (dryRun) => {
    setLoading(true);
    try {
      const data = await apiPost(
        `/project-planning/plans/${planId}/material-sheet/backfill-from-pos`,
        { dry_run: dryRun }
      );
      setPreview(data);
      if (!dryRun && data.ok) {
        setCommitted(true);
        toast.success(`Backfilled ${data.committed} item(s) from existing POs`);
      }
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Backfill failed');
    } finally { setLoading(false); }
  };

  const runExcelBackfill = async (dryRun) => {
    if (!excelFile) { toast.error('Please select an Excel file first'); return; }
    setLoading(true);
    try {
      const form = new FormData();
      form.append('file', excelFile);
      const data = await apiUpload(`/project-planning/plans/${planId}/material-sheet/backfill-from-excel?dry_run=${dryRun ? 'true' : 'false'}`, form);
      setPreview(data);
      if (!dryRun && data.ok) {
        setCommitted(true);
        toast.success(`Backfilled ${data.committed} item(s) from Excel`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Excel backfill failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="pp-pms-backfill-wizard">
      <div className="bg-white rounded-sm w-full max-w-2xl shadow-xl border border-slate-200 flex flex-col max-h-[88vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Upload size={14} className="text-indigo-600" />
            <span className="text-sm font-display font-bold text-[#3b4872]">Backfill Ordered Qty</span>
          </div>
          <button onClick={onClose}><X size={16} className="text-slate-400 hover:text-slate-700" /></button>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Mode selector */}
          <div className="flex gap-3">
            {[
              { v: 'pos', label: 'From Existing POs', icon: <PackageCheck size={14} />, sub: 'Auto-match approved POs in the system' },
              { v: 'excel', label: 'Upload Excel', icon: <FileSpreadsheet size={14} />, sub: 'Megafine / manual PO data export' },
            ].map(opt => (
              <label key={opt.v} className={`flex-1 flex items-start gap-2 border rounded p-3 cursor-pointer ${mode === opt.v ? 'bg-indigo-50 border-indigo-300' : 'border-slate-200 hover:border-slate-300'}`}>
                <input type="radio" name="backfill_mode" value={opt.v} checked={mode === opt.v}
                  onChange={() => { setMode(opt.v); setPreview(null); setCommitted(false); }}
                  className="mt-0.5 accent-indigo-600" />
                <div>
                  <div className="flex items-center gap-1.5 text-xs font-display font-semibold text-slate-700">{opt.icon}{opt.label}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{opt.sub}</div>
                </div>
              </label>
            ))}
          </div>

          {mode === 'excel' && (
            <div>
              <label className="block text-xs font-display font-bold text-slate-600 mb-2">
                Select Excel File
                <span className="ml-2 text-[10px] text-slate-400 font-normal">
                  (Required columns: Material Name/Description, Qty/Ordered Qty)
                </span>
              </label>
              <input ref={fileRef} type="file" accept=".xlsx,.xls"
                onChange={e => { setExcelFile(e.target.files?.[0] || null); setPreview(null); }}
                className="block w-full text-xs text-slate-600 border border-slate-300 rounded px-3 py-2 file:mr-3 file:text-xs file:font-bold file:border-0 file:bg-indigo-600 file:text-white file:py-1 file:px-3 file:rounded"
                data-testid="pp-pms-backfill-file" />
              {excelFile && (
                <div className="mt-1 text-[10px] text-indigo-600">{excelFile.name} ({Math.round(excelFile.size / 1024)} KB)</div>
              )}
            </div>
          )}

          {!preview && !committed && (
            <button
              onClick={() => mode === 'pos' ? runPosBackfill(true) : runExcelBackfill(true)}
              disabled={loading || (mode === 'excel' && !excelFile)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-display font-bold bg-slate-50 border border-slate-300 rounded-sm hover:bg-slate-100 disabled:opacity-50"
              data-testid="pp-pms-backfill-preview-btn">
              {loading ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
              Preview Matches (Dry Run)
            </button>
          )}

          {/* Preview table */}
          {preview && (
            <div>
              <div className="text-xs font-display font-bold text-slate-700 mb-2">
                Preview — {preview.matched_count ?? preview.preview?.filter(r => r.matched !== false && r.delta > 0).length ?? 0} items to update
              </div>
              <div className="border border-slate-200 rounded overflow-hidden max-h-64 overflow-y-auto">
                <table className="w-full text-[10px]">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-1.5 text-left font-display font-bold text-slate-400">Material</th>
                      <th className="px-2 py-1.5 text-right font-display font-bold text-slate-400">Old Qty</th>
                      <th className="px-2 py-1.5 text-right font-display font-bold text-slate-400">New Qty</th>
                      <th className="px-2 py-1.5 text-right font-display font-bold text-slate-400">Delta</th>
                      <th className="px-3 py-1.5 text-left font-display font-bold text-slate-400">Source</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {(preview.preview || []).map((row, i) => (
                      <tr key={i} className={row.matched === false ? 'opacity-40' : ''}>
                        <td className="px-3 py-1.5 font-display font-semibold text-slate-700 max-w-[200px] truncate">
                          {row.pms_item_name || row.excel_name}
                          {row.matched === false && <span className="ml-1 text-[9px] text-slate-400">(unmatched)</span>}
                        </td>
                        <td className="px-2 py-1.5 text-right text-slate-500">{row.old_ordered_qty ?? '—'}</td>
                        <td className="px-2 py-1.5 text-right font-semibold text-indigo-700">{row.new_ordered_qty ?? '—'}</td>
                        <td className={`px-2 py-1.5 text-right font-bold ${(row.delta || 0) > 0 ? 'text-emerald-600' : 'text-slate-300'}`}>
                          {(row.delta || 0) > 0 ? `+${row.delta}` : '—'}
                        </td>
                        <td className="px-3 py-1.5 text-slate-400 max-w-[120px] truncate">
                          {row.po_nos?.join(', ') || (row.match_score ? `${row.match_score}% match` : '')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {committed && (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-4 py-3">
              <CheckCircle2 size={16} className="text-emerald-600" />
              <span className="text-sm text-emerald-700 font-semibold">Backfill committed successfully!</span>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 bg-slate-50 border border-slate-300 rounded-sm hover:bg-slate-100">
            {committed ? 'Close' : 'Cancel'}
          </button>
          {preview && !committed && (
            <button
              onClick={() => mode === 'pos' ? runPosBackfill(false) : runExcelBackfill(false)}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-display font-bold bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm disabled:opacity-50"
              data-testid="pp-pms-backfill-commit-btn">
              {loading ? <Loader2 size={13} className="animate-spin" /> : <CheckCheck size={13} />}
              Commit Backfill
            </button>
          )}
          {committed && (
            <button onClick={onDone} className="px-4 py-2 text-sm font-display font-bold bg-[#3b4872] text-white rounded-sm hover:bg-[#2d3856]">
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


/* ── Reject modal ─────────────────────────────────────────────────── */
function RejectModal({ reason, setReason, onCancel, onSubmit }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="pp-pms-reject-modal">
      <div className="bg-white rounded-sm w-full max-w-md shadow-xl border border-slate-200">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <span className="text-sm font-display font-bold text-rose-700">Reject Material Sheet</span>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-4">
          <label className="block text-xs font-display font-semibold text-slate-700 mb-2">
            Rejection reason <span className="text-rose-600">*</span>
          </label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={4}
            className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-rose-400"
            placeholder="Explain what needs to be revised…"
            data-testid="pp-pms-reject-reason" />
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-slate-600 bg-slate-50 border border-slate-300 rounded-sm">
            Cancel
          </button>
          <button onClick={onSubmit}
            className="px-4 py-2 text-sm font-display font-bold bg-rose-600 hover:bg-rose-700 text-white rounded-sm"
            data-testid="pp-pms-reject-submit-btn">
            Reject &amp; Send Back
          </button>
        </div>
      </div>
    </div>
  );
}
